/* eslint-disable camelcase */
import * as React from 'react';

import styled from '@emotion/styled';
import { Button as BaseButton } from '@lightspeed/flame/Button';
import { FlameTheme } from '@lightspeed/flame/Core';
import { IconDelete } from '@lightspeed/flame/Icon/Delete';

import Format from '../app/src/data/Format';
import { createReactRenderer } from 'helpers/react-renderer';

import { BulkDeleteFn, bulkDeletePOItems } from './BulkDeleteAPI';

const Button = styled(BaseButton)`
  font-size: 12px;
  padding: 4px 8px 4px 4px;
  min-height: 26px;
  margin-left: 10px;
`;

type RegisteredListings = 'purchase_listings_items_view';

const deleteFunctions: Record<RegisteredListings, BulkDeleteFn> = {
  purchase_listings_items_view: bulkDeletePOItems,
};

type Props = {
  listingId: RegisteredListings;
};

const BulkDeleteButton: React.FC<Props> = ({ listingId }) => {
  const deleteFn = deleteFunctions[listingId];
  const checkAllRef = React.useRef<HTMLInputElement>(null);
  const [selectCount, setSelectCount] = React.useState(0);

  const handleSingleSelect: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSelectCount((prev) => Math.max(0, event.target.checked ? prev + 1 : prev - 1));
  };

  const handleSelectAll: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    window.listingToggleAll(event.target as unknown as string);
    setSelectCount($(`input.${listingId}_rec_sel:checked`).length);
  };

  const handleBulkDelete = async () => {
    const ids = $(`input.${listingId}_rec_sel:checked`)
      .map((_, elem) => Format.parseInt(elem.name as string, -1))
      .filter((_, name) => name > -1)
      .toArray() as number[];

    window.showNotifier('working');

    await deleteFn(ids, (message) => {
      window.showNotifier('warning', message);
    });
    window.openView();
    window.hideNotifier('working');
  };

  const selectZero = () => {
    setSelectCount(0);
    $(`input.${listingId}_rec_sel`).prop('checked', false);
  };

  React.useEffect(() => {
    if (!deleteFn) return () => {};

    // Add count watchers to row checkboxes
    const checkboxes = $(`input.${listingId}_rec_sel`);
    checkboxes.on('change', handleSingleSelect as any);

    // Add count watcher to row delete buttons
    const deleteButtons = $(`#${listingId} button.delete`);
    deleteButtons.on('click', selectZero);

    return () => {
      checkboxes.off('change', handleSingleSelect as any);
      deleteButtons.off('click', selectZero);
    };
  }, [listingId, deleteFn, selectZero, handleSingleSelect]);

  React.useEffect(() => {
    if (!checkAllRef.current) return;

    // As the select count changes, the state of the select all
    // checkbox changes between all, none, and indeterminate
    const items = $(`input.${listingId}_rec_sel`);
    if (items.length === selectCount) {
      checkAllRef.current.checked = true;
      checkAllRef.current.indeterminate = false;
    } else if (selectCount === 0) {
      checkAllRef.current.checked = false;
      checkAllRef.current.indeterminate = false;
    } else {
      checkAllRef.current.indeterminate = true;
    }
  }, [selectCount]);

  if (!deleteFn) return <></>;

  return (
    <>
      <input
        type="checkbox"
        onChange={handleSelectAll}
        data-pannel={listingId}
        ref={checkAllRef}
        checked={selectCount !== 0}
        id={`${listingId}_rec_sel_all`}
        title="Select all items on current page"
      />
      <FlameTheme>
        {selectCount > 0 && (
          <Button
            type="button"
            onClick={handleBulkDelete}
            id={`${listingId}_bulk_delete`}
            title={`Delete <var>${selectCount}</var> ${selectCount > 1 ? 'items' : 'item'}`}
          >
            <IconDelete size="12px" />
            <span data-automation="bulk_delete_button_label">
              (<var>{selectCount}</var> {selectCount > 1 ? 'items' : 'item'})
            </span>
          </Button>
        )}
      </FlameTheme>
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: BulkDeleteButton,
  selector: '[data-react-root="BulkDeleteButton"]',
});

history.onChangeState(
  [{ name: 'purchase.views.purchase', form_name: 'view', id: undefined }],
  () => RenderedComponent.mount(),
  () => RenderedComponent.unmount()
);

export default BulkDeleteButton;
